import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MapPin, MousePointerClick, Eye, Package, Activity, Target } from "lucide-react";
import { Navbar, Footer } from "@/components/Chrome";
import { api, money2, num, CAMPAIGN_STATUS, LOAD_STATUS } from "@/lib/spott";

const OBJ_LABEL = { awareness: "Awareness", conversion: "Conversión", expansion: "Expansión regional" };

const Metric = ({ l, v, icon: Icon }) => (
  <div className="rounded-xl border border-spott-mid bg-white p-5">
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {Icon && <Icon size={14} className="text-spott-yellow" />} {l}
    </div>
    <div className="mt-2 text-xl font-bold text-spott-blue">{v}</div>
  </div>
);

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    document.title = "Mis campañas — Spott";
    api.get("/campaigns").then(({ data }) => {
      setCampaigns(data);
      if (data.length) setOpenId(data[0].campaign_id);
    });
  }, []);

  const c = campaigns.find((x) => x.campaign_id === openId);

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="relative overflow-hidden bg-spott-blue py-14 grain">
        <div className="arc-deco -left-24 -bottom-28 h-80 w-80" />
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Reportería de campañas</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">Mis campañas</h1>
          <p className="mt-2 text-sm font-light text-spott-mid">
            Ejecución, avance y desempeño digital de cada campaña contratada con Spott.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-10">
        {campaigns[0]?.is_demo && (
          <p className="mb-6 rounded-xl border border-spott-yellow bg-spott-yellow/10 px-5 py-3 text-sm text-spott-blue"
            data-testid="demo-banner">
            Estás viendo <strong>campañas de demostración</strong>. Cuando tus campañas se activen, verás aquí tu
            data real, incluida la de medios digitales enviada por cada retailer.
          </p>
        )}

        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="campaign-tabs">
          {campaigns.map((x) => (
            <button key={x.campaign_id} onClick={() => setOpenId(x.campaign_id)}
              data-testid={`campaign-tab-${x.campaign_id}`}
              className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                openId === x.campaign_id ? "bg-spott-blue text-white"
                  : "border border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
              }`}>
              {x.name}
            </button>
          ))}
        </div>

        {c && (
          <div className="mt-8" data-testid="campaign-detail">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-spott-blue sm:text-2xl">{c.name}</h2>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${CAMPAIGN_STATUS[c.status].cls}`}
                data-testid="campaign-status">{CAMPAIGN_STATUS[c.status].label}</span>
              <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-spott-blue">
                <Target size={12} className="text-spott-yellow" /> {OBJ_LABEL[c.objective] || c.objective}
              </span>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric l="Espacios contratados" v={`${c.spaces_count} (${c.units_total} unidades)`} icon={Package} />
              <Metric l="Impacto esperado" v={num(c.expected_reach)} icon={Eye} />
              <Metric l="Inversión" v={money2(c.budget)} icon={Activity} />
              <Metric l="Ciudades" v={c.cities.length} icon={MapPin} />
            </div>

            {/* timeline */}
            <div className="mt-6 rounded-xl border border-spott-mid bg-white p-6" data-testid="campaign-timeline">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-spott-blue">
                  <CalendarClock size={16} className="text-spott-yellow" /> Timeline de campaña
                </h3>
                <span className="text-sm font-semibold text-spott-blue" data-testid="campaign-progress">
                  {c.progress_percent}% transcurrido · {c.days_remaining} día(s) restantes
                </span>
              </div>
              <div className="mt-4 h-3 overflow-hidden rounded-full bg-spott-light">
                <div className="h-full rounded-full bg-spott-yellow transition-all"
                  style={{ width: `${c.progress_percent}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                <span>Inicio {c.start_date}</span>
                <span>{c.days_elapsed} de {c.days_total} días</span>
                <span>Fin {c.end_date}</span>
              </div>
            </div>

            {/* estados */}
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              <div className="rounded-xl border border-spott-mid bg-white p-6" data-testid="campaign-status-breakdown">
                <h3 className="text-sm font-bold uppercase tracking-wide text-spott-blue">Estado de ejecución</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {Object.entries(c.by_status).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between text-gray-600">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${CAMPAIGN_STATUS[k].cls}`}>
                        {CAMPAIGN_STATUS[k].label}
                      </span>
                      <span className="font-semibold text-spott-blue">{v} espacio(s)</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-spott-mid bg-white p-6" data-testid="campaign-load-breakdown">
                <h3 className="text-sm font-bold uppercase tracking-wide text-spott-blue">
                  Carga de producto (solo ejecutados)
                </h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {Object.entries(c.by_load).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between text-gray-600">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${LOAD_STATUS[k].cls}`}>
                        {LOAD_STATUS[k].label}
                      </span>
                      <span className="font-semibold text-spott-blue">{v} espacio(s)</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* digital */}
            {c.digital_metrics && (
              <div className="mt-6" data-testid="campaign-digital">
                <h3 className="text-lg font-bold text-spott-blue">Desempeño de medios digitales</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {c.digital_spaces} espacio(s) digital(es) con data enviada por los retailers. Los formatos físicos
                  no reportan métricas digitales.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Metric l="Impresiones" v={num(c.digital_metrics.impressions)} icon={Eye} />
                  <Metric l="Clicks / interacciones" v={num(c.digital_metrics.clicks)} icon={MousePointerClick} />
                  <Metric l="CTR" v={`${c.digital_metrics.ctr}%`} icon={Activity} />
                  <Metric l="Vistas completas" v={num(c.digital_metrics.completed_views)} icon={Eye} />
                  <Metric l="VTR" v={`${c.digital_metrics.vtr}%`} icon={Activity} />
                  <Metric l="Alcance único" v={num(c.digital_metrics.unique_reach)} icon={MapPin} />
                </div>
              </div>
            )}

            {/* espacios */}
            <h3 className="mt-10 text-lg font-bold text-spott-blue">Detalle de espacios contratados</h3>
            <div className="mt-5 space-y-4" data-testid="campaign-spaces">
              {c.spaces.map((s) => (
                <div key={s.space_id} className="rounded-xl border border-spott-mid bg-white p-6"
                  data-testid={`campaign-space-${s.space_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h4 className="text-base font-semibold text-spott-blue">
                        {s.units}× {s.name}
                      </h4>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                        <MapPin size={12} /> {s.retailer_name} · {s.city} · {s.address}
                      </p>
                      <p className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-spott-light px-3 py-1 font-medium text-spott-blue">
                          {s.format}
                        </span>
                        <span className="rounded-full bg-spott-light px-3 py-1 font-medium text-spott-blue">
                          {s.funnel_stage === "awareness" ? "Awareness" : "Conversión"}
                        </span>
                        <span className={`rounded-full px-3 py-1 font-semibold ${CAMPAIGN_STATUS[s.status].cls}`}>
                          {CAMPAIGN_STATUS[s.status].label}
                        </span>
                        {s.status === "ejecutado" && s.load_status && (
                          <span className={`rounded-full px-3 py-1 font-semibold ${LOAD_STATUS[s.load_status].cls}`}
                            data-testid={`load-status-${s.space_id}`}>
                            {LOAD_STATUS[s.load_status].label}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Impacto esperado</div>
                      <div className="text-lg font-bold text-spott-blue">{num(s.expected_reach)}</div>
                    </div>
                  </div>
                  {s.is_digital && s.metrics && (
                    <div className="mt-5 grid gap-3 border-t border-spott-mid pt-4 sm:grid-cols-3 lg:grid-cols-6"
                      data-testid={`digital-metrics-${s.space_id}`}>
                      {[
                        ["Impresiones", num(s.metrics.impressions)],
                        ["Clicks", num(s.metrics.clicks)],
                        ["CTR", `${s.metrics.ctr}%`],
                        ["Vistas completas", num(s.metrics.completed_views)],
                        ["VTR", `${s.metrics.vtr}%`],
                        ["Frecuencia", s.metrics.frequency],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div className="text-[11px] text-gray-500">{l}</div>
                          <div className="text-sm font-bold text-spott-blue">{v}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {s.is_digital && !s.metrics && (
                    <p className="mt-4 border-t border-spott-mid pt-4 text-xs text-gray-500">
                      Espacio digital sin data reportada aún por el retailer.
                    </p>
                  )}
                  {!s.is_digital && (
                    <p className="mt-4 border-t border-spott-mid pt-4 text-xs text-gray-500">
                      Formato físico: la medición se realiza con auditoría en campo y evidencia fotográfica.
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/cotizar" data-testid="campaigns-new-quote"
                className="rounded-full bg-spott-yellow px-6 py-3.5 text-sm font-bold text-spott-blue">
                Cotizar otra campaña
              </Link>
              <Link to="/recompensas" data-testid="campaigns-rewards-link"
                className="rounded-full border border-spott-mid bg-white px-6 py-3.5 text-sm font-bold text-spott-blue">
                Ver mis puntos Spott
              </Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
